import {
  cloneElement,
  isValidElement,
  type ReactElement,
  type ReactNode,
} from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { tokens } from "./primitives";

export type SectionId =
  | "branding"
  | "payment"
  | "shipping"
  | "inventory"
  | "policies"
  | "team"
  | "domain";

export type SectionDef = {
  id: SectionId;
  label: string;
  desc: string;
  icon: ReactNode;
};

export type SectionStatus = Record<SectionId, { done: number; total: number }>;

export function SectionNav({
  sections,
  section,
  setSection,
  tip,
  isMobile,
}: {
  sections: SectionDef[];
  section: SectionId;
  setSection: (id: SectionId) => void;
  tip?: string;
  isMobile?: boolean;
}) {
  const t = useTranslations();
  const ss = keys.seller.storeSettings;
  if (isMobile) {
    return (
      <nav
        className="no-scrollbar"
        style={{
          display: "flex",
          gap: 8,
          overflowX: "auto",
          margin: "0 -16px",
          padding: "0 16px 2px",
        }}
      >
        {sections.map((s) => {
          const active = section === s.id;
          const icon = isValidElement(s.icon)
            ? cloneElement(s.icon as ReactElement<{ width?: number; height?: number }>, { width: 14, height: 14 })
            : s.icon;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => setSection(s.id)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                flexShrink: 0,
                whiteSpace: "nowrap",
                padding: "8px 13px",
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                background: active ? tokens.navy : tokens.surface,
                color: active ? "#fff" : tokens.text2,
                border: `1px solid ${active ? tokens.navy : tokens.border}`,
              }}
            >
              <span style={{ display: "flex", opacity: active ? 1 : 0.7 }}>
                {icon}
              </span>
              {s.label}
            </button>
          );
        })}
      </nav>
    );
  }

  return (
    <aside style={{ position: "sticky", top: 16, alignSelf: "start" }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 0.8,
          textTransform: "uppercase",
          color: tokens.text3,
          marginBottom: 10,
        }}
      >
        {t(ss.navHeader)}
      </div>
      <nav style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {sections.map((s) => {
          const active = section === s.id;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => setSection(s.id)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 12px",
                borderRadius: 10,
                width: "100%",
                textAlign: "start",
                background: active ? tokens.surface : "transparent",
                border: `1px solid ${active ? tokens.borderStrong : "transparent"}`,
                boxShadow: active ? "0 1px 2px rgba(15,20,40,0.04)" : "none",
                cursor: "pointer",
                color: active ? tokens.text : tokens.text2,
              }}
            >
              <span
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: active
                    ? `color-mix(in oklab, ${tokens.accent} 14%, white)`
                    : tokens.bgSoft,
                  color: active ? tokens.accent2 : tokens.text2,
                  border: `1px solid ${active ? `color-mix(in oklab, ${tokens.accent} 25%, white)` : tokens.border}`,
                }}
              >
                {s.icon}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="text-card" style={{ fontWeight: 600 }}>
                  {s.label}
                </div>
                <div
                  className="text-chip"
                  style={{
                    color: tokens.text3,
                    marginTop: 1,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {s.desc}
                </div>
              </div>
            </button>
          );
        })}
      </nav>

      {tip ? (
        <div
          style={{
            marginTop: 16,
            padding: "12px 14px",
            borderRadius: 10,
            background: `color-mix(in oklab, ${tokens.accent} 6%, white)`,
            border: `1px solid color-mix(in oklab, ${tokens.accent} 18%, white)`,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 12,
              fontWeight: 600,
              color: tokens.accent2,
              marginBottom: 4,
            }}
          >
            <span>💡</span> {t(ss.tipLabel)}
          </div>
          <div style={{ fontSize: 12, color: tokens.text2, lineHeight: 1.5 }}>
            {tip}
          </div>
        </div>
      ) : null}
    </aside>
  );
}
